import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonGroup,
  Container,
  Row,
  Col,
  Spinner,
  Badge,
} from 'reactstrap';
import StepRangeSlider from 'react-step-range-slider';
import { useStripe } from '@stripe/react-stripe-js';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ProgressBar, Step } from 'react-step-progress-bar';
import styled from 'styled-components';
import Slider, { SliderTooltip } from 'rc-slider';

import clsx from 'clsx';

import MainLayout from '../../layouts/MainLayout';
import useQueryParams from '../../hooks/useQuery';
import { fetchPackages } from '../../api/actions/stripe';
import { fetcher } from '../../api/client';
import { changePlan } from '../../api/actions/profile';
import { useChangePlan, useProfile } from '../../api/hooks/profile';
import { usePackages } from '../../api/hooks/stripe';
import Title from '../../components/Title';

import Promo from '../../components/Promo';

import { getPrice } from '../../utils';
import config from '../../config';
import { oneTimeProducts, recurringProducts } from './constants';

import Select from 'react-select';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLanguages } from '../../hooks/useLanguages';

const DISCOUNT = 0;

export function OneTimePayment() {
  const { t } = useTranslation();
  const stripe = useStripe();
  const currency = 'mxn';
  const { profile } = useProfile();
  const [plans, setPlans] = useState({});
  const [data, setData] = useState([]);
  const [currentStorageSelected, setCurrentStorageSelected] = useState({});
  const meta = {
    productId: 'contracts_level_1',
    type: 'good',
    currency,
  };
  const { isLoading } = usePackages(meta, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const sortedData = data?.data?.sort((a, b) => a.amount - b.amount);
      setData(sortedData);
      setCurrentStorageSelected(sortedData[0]);
    },
  });

  const handleCurrentSelectedChange = (storageSize) => {
    setCurrentStorageSelected(storageSize);
  };

  const handlePurchase = async (sku) => {
    console.log({ sku });
    const { id, name, object, quantity } = sku;
    await stripe.redirectToCheckout({
      successUrl: `${
        config.baseUrl
      }/checkout/success?productId=${id}&productName=${encodeURIComponent(
        name
      )}`,
      cancelUrl: `${config.baseUrl}/plans/one-time?currency=${currency}`,
      mode: 'payment',
      lineItems: [
        {
          price: id,
          quantity,
        },
      ],
      clientReferenceId: profile.organization,
      customerEmail: profile.email,
      // locale: 'mx'
    });
  };

  return (
    <Container class="py-4 mb-4 px-4">
      <Row className="mt-4 justify-content-center">
        <Col sm={6} className="mb-4">
          <ShieldItemContainer
            className="h-100"
            name={t(`packages.contracts_level_1.title`)}
            benefits={Object.values(
              t(`packages.contracts_level_1.benefits`, { returnObjects: true })
            )}
            productId="contracts_level_1"
            discount={DISCOUNT}
            storageSizes={data}
            isLoading={isLoading}
            currentStorageSelected={currentStorageSelected}
            onPurchase={handlePurchase}
            onCurrentStorageSelectedChange={handleCurrentSelectedChange}
          />
        </Col>
        <Col sm={6} className="mb-4">
          <ShieldItemContainer
            className="h-100"
            name={t(`packages.contracts_level_2.title`)}
            benefits={Object.values(
              t(`packages.contracts_level_2.benefits`, { returnObjects: true })
            )}
            productId="contracts_level_2"
            storageSizes={data}
            discount={DISCOUNT}
            isLoading={isLoading}
            currentStorageSelected={currentStorageSelected}
            onPurchase={handlePurchase}
            onCurrentStorageSelectedChange={handleCurrentSelectedChange}
          />
        </Col>
        <Col sm={6} className="mb-4">
          <ShieldItemContainer
            className="h-100"
            name={t(`packages.contracts_level_3.title`)}
            benefits={Object.values(
              t(`packages.contracts_level_3.benefits`, { returnObjects: true })
            )}
            productId="contracts_level_3"
            storageSizes={data}
            // discount={DISCOUNT}
            isLoading={isLoading}
            currentStorageSelected={currentStorageSelected}
            onPurchase={handlePurchase}
            onCurrentStorageSelectedChange={handleCurrentSelectedChange}
          />
        </Col>
        <Col sm={6} className="mb-4">
          <ShieldItemContainer
            className="h-100"
            name={t(`packages.signature_fiels.title`)}
            benefits={Object.values(
              t(`packages.signature_fiels.benefits`, { returnObjects: true })
            )}
            productId="signature_fiels"
            storageSizes={data}
            isLoading={isLoading}
            currentStorageSelected={currentStorageSelected}
            onPurchase={handlePurchase}
            onCurrentStorageSelectedChange={handleCurrentSelectedChange}
          />
        </Col>
      </Row>
    </Container>
  );
}

function getPackageQuantity(currentPackage = {}) {
  return currentPackage?.id?.split?.('_')[0];
}

function ShieldItemContainer(props) {
  const { t } = useTranslation('common');
  const {
    className,
    name,
    currency = 'mxn',
    productId,
    benefits = [],
    currentStorageSelected = {},
    discount,
    onPurchase,
  } = props;
  const [data, setData] = useState({});
  const [quantity, setQuantity] = useState(0);
  const [currentPackage, setCurrentPackage] = useState({});
  const meta = {
    type: 'good',
    productId,
    currency,
  };
  console.log({ benefits });

  const { isLoading } = usePackages(meta, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const sortedData = data?.data
        ?.filter((p) => !p.id.includes('1_contracts_level'))
        .sort((a, b) => a.attributes?.index - b?.attributes?.index);
      const totalPackages = sortedData.length - 1;
      const valuePerPackage = 100 / totalPackages;
      const marks = sortedData.reduce((obj, data, i) => {
        obj[Math.round(i * valuePerPackage)] = data;
        return obj;
      }, {});

      setData({
        min: getPackageQuantity(sortedData[0]),
        marks,
        max: getPackageQuantity(sortedData[sortedData.length - 1]),
      });
      // setCurrentPackage(sortedData[0]);
      setQuantity(Number(getPackageQuantity(sortedData[0])));
    },
  });
  const { isLoading: isLoading2 } = usePackages(
    { ...meta, type: 'price' },
    {
      refetchOnWindowFocus: false,
      onSuccess: ({ data }) => {
        setCurrentPackage(data[0]);
      },
    }
  );

  const handlePurchase = () => {
    currentPackage.name = name;
    currentPackage.quantity = quantity;
    onPurchase(currentPackage);
  };

  const handleChange = (value) => {
    console.log({ value });
    console.log(value, data.marks[value]);
    // setCurrentPackage(data.marks[value]);
    setQuantity(Number(getPackageQuantity(data.marks[value])));
  };

  console.log({ name, currentPackage, quantity });

  return (
    <div
      className={clsx(
        'rounded-lg bg-white d-flex flex-column justify-content-between p-4 flex-auto',
        className
      )}
    >
      <div className="">
        <div className="pb-3 border-bottom">
          <h3 className="font-weight-bold font-size-24">{name}</h3>
          <div
            className="font-weight-bold font-size-20 py-2 mt-2"
            hidden={isLoading}
          >
            <div className="d-flex align-items-center" hidden={!discount}>
              <span
                className={clsx(discount && 'text-muted text-strikethrough')}
              >
                {getPrice(
                  discount
                    ? (currentPackage?.unit_amount * quantity) / discount
                    : currentPackage?.unit_amount * quantity,
                  currentPackage?.currency,
                  true
                )}
              </span>{' '}
              <Badge
                className="ml-2"
                color="accent"
                size="xl"
                hidden={!discount || isLoading}
              >
                {discount}%
              </Badge>
            </div>
            <span>
              {getPrice(
                currentPackage?.unit_amount * quantity,
                currentPackage?.currency,
                true
              )}{' '}
            </span>

            <span className="text-muted font-size-12 ml-2">
              {quantity} {t('Plans.signatures')}
            </span>
          </div>
          <div className="font-size-14 text-accent" hidden={!discount}>
            Vigencia al 15 de octubre del 2022
          </div>
          <div className="text-center" hidden={!isLoading}>
            <Spinner />
          </div>
        </div>
        <div className="p-4">
          <Row>
            {benefits.map((benefit) => {
              return (
                <div className="d-flex p-2">
                  <div>
                    <FontAwesomeIcon
                      icon="check-circle"
                      className="text-accent"
                      size="lg"
                    />
                  </div>
                  <div className="ml-2 text-muted">{benefit}</div>
                </div>
              );
            })}
          </Row>
        </div>
      </div>
      <div className="w-100">
        <div className="d-flex my-2 mb-4" hidden={isLoading}>
          <div className="mr-3">{data.min}</div>
          <div className="w-100 p-2">
            <Slider
              min={0}
              defaultValue={0}
              marks={data.marks}
              step={null}
              onChange={handleChange}
              trackStyle={{ background: '#f47b33' }}
              activeDotStyle={{ background: '#f47b33', borderColor: '#f47b33' }}
              handleStyle={{
                background: '#f47b33',
                borderColor: '#f47b33',
                boxShadow: '0 0 5px #f47b33',
              }}
            />
          </div>
          <div className="ml-3">{data.max}</div>
        </div>
        <Button
          className="w-100"
          color={'accent-100'}
          disabled={isLoading}
          onClick={handlePurchase}
        >
          {t('Plans.buttons.buy')}
        </Button>
      </div>
    </div>
  );
}

function StoragePill({ label, isActive, onClick }) {
  return (
    <div
      className={clsx(
        'd-flex p-2 bg-accent-100 rounded cursor-pointer',
        !isActive && 'opacity-75'
      )}
      onClick={onClick}
    >
      <div className="ml-2 text-white font-size-16">{label}</div>
    </div>
  );
}

const StyledStep = styled(Step)`
  color: white;
  width: 20px;
  height: 20px;
  font-size: 12px;
  background-color: rgba(211, 211, 211, 0.8);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  &.accomplished {
    background-color: rgba(0, 116, 217, 1);
  }
`;

// function Stepper({
//    range = [],
// }) {
//   const length = range.length;
//   const percentPerStep = 100/(length - 1);
//   const [currentStep, setCurrentStep] = useState(1)

//   const

//   return (
//     <ProgressBar className="w-100" height={5} filledBackground="#f47b33" percent={Math.min(percentPerStep * (currentStep - 1) + 1, 100)} hasStepZero>
//     {
//       range.map((step) => (
//         <StyledStep>
//         {({ accomplished, index }) => (
//           <div
//             className={`indexedStep position-relative ${accomplished ? "accomplished pointer" : null}`}
//             onClick={currentStep > index + 1 ? step.onClick : undefined}
//             style={{width: 15, height: 15}}
//           >
//             <div className="position-absolute text-black-50 text-center" style={{top: 40}}>{step.title}</div>
//           </div>
//         )}
//       </StyledStep>
//       ))
//     }
//   </ProgressBar>
//   )
// }
